import { announcementSettings } from "../../../Core/Content/announcement.mjs";
import AppService from "../../../service/app.service";
import { useEffect, useRef, useState } from "react";
import {
  getDraft,
  saveDraft,
  publish,
  uploadMedia,
} from "../../../Core/Content/adapter";
import {
  DraftEnvelope,
  PageContent,
  PageId,
} from "../../../Core/Content/Types";
import { isDirtyFn, validatePageFn } from "./Functions";
export function usePageEditor(pageId: PageId) {
  const [saved, setSaved] = useState<DraftEnvelope | null>(null);
  const [content, setContent] = useState<PageContent | null>(null);
  const [busy, setBusy] = useState(true);
  const [message, setMessage] = useState("");
  const [failed, setFailed] = useState(false);
  const [shared, setShared] = useState<{
    footer?: Partial<PageContent>;
    navigation?: PageContent["navigation"];
    announcement?: PageContent["announcement"];
  }>({});
  const [uploading, setUploading] = useState(false);
  const [menu, setMenu] = useState<PageContent | null>(null);
  const inFlight = useRef(false);
  useEffect(() => {
    if (pageId !== "home") return;
    let active = true;
    getDraft("menu").then((result) => {
      if (active && result.ok) setMenu(result.value.draft.content);
    });
    return () => {
      active = false;
    };
  }, [pageId]);
  useEffect(() => {
    let active = true;
    const service = new AppService();
    Promise.all([
      service.getPage("footer"),
      fetch("/api/cms/pages/navigation/published").then((r) =>
        r.ok ? r.json() : null
      ),
    ])
      .then(([footer, navigation]) => {
        if (active)
          setShared({
            footer: footer.data || undefined,
            navigation: navigation?.value?.content?.navigation,
            announcement: announcementSettings(navigation?.value?.content),
          });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  const dirty = !!content && !!saved && isDirtyFn(content, saved.draft.content);
  const errors = content ? validatePageFn(content, pageId) : [];
  useEffect(() => {
    let active = true;
    setBusy(true);
    getDraft(pageId).then((result) => {
      if (!active) return;
      if (result.ok) {
        setSaved(result.value);
        setContent(result.value.draft.content);
      } else {
        setMessage(result.message);
        setFailed(true);
      }
      setBusy(false);
    });
    return () => {
      active = false;
    };
  }, [pageId]);
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);
  async function save() {
    if (!content || !saved || errors.length || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setMessage("");
    const snapshot = content;
    const result = await saveDraft(pageId, snapshot, saved.draft.revision);
    if (result.ok) {
      setSaved({ ...saved, draft: result.value });
      setMessage("Draft saved. Public content has not changed.");
    } else setMessage(result.message);
    setFailed(!result.ok);
    setBusy(false);
    inFlight.current = false;
  }
  async function publishSaved() {
    if (!saved || dirty || errors.length || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setMessage("");
    const result = await publish(
      pageId,
      saved.draft.revision,
      saved.publishedRevision
    );
    if (result.ok) {
      setSaved({ ...saved, publishedRevision: result.value.revision });
      try {
        localStorage.removeItem(pageId);
      } catch {}
      setMessage("Published successfully.");
    } else setMessage(result.message);
    setFailed(!result.ok);
    setBusy(false);
    inFlight.current = false;
  }
  async function upload(file: File, onComplete: (url: string) => void) {
    if (inFlight.current) return;
    inFlight.current = true;
    setUploading(true);
    const result = await uploadMedia(file);
    if (result.ok) {
      onComplete(result.value.url);
      setMessage("File uploaded. Save your draft to keep this change.");
    } else setMessage(result.message);
    setFailed(!result.ok);
    setUploading(false);
    inFlight.current = false;
  }
  function reset() {
    if (
      saved &&
      (!dirty ||
        window.confirm("Discard unsaved changes and restore your saved draft?"))
    )
      setContent(saved.draft.content);
  }
  return {
    shared,
    upload,
    uploading,
    menu,
    content,
    setContent,
    saved,
    busy,
    message,
    failed,
    dirty,
    errors,
    save,
    publishSaved,
    reset,
  };
}
